import dayjs from 'dayjs';

const DEFAULT_NOTIFICATION_BY_TAB = {
  order: [],
  loyalty: [],
  info: [],
};
const TAB_KEYS = Object.keys(DEFAULT_NOTIFICATION_BY_TAB);
const getEmptyNotificationsByTab = () => ({
  order: [],
  loyalty: [],
  info: [],
});

const TAB_KEY_BY_ALIAS = {
  order: 'order',
  orders: 'order',
  pesanan: 'order',
  transaction: 'order',
  transactions: 'order',
  loyalty: 'loyalty',
  reward: 'loyalty',
  rewards: 'loyalty',
  membership: 'loyalty',
  points: 'loyalty',
  point: 'loyalty',
  info: 'info',
  information: 'info',
  umum: 'info',
  general: 'info',
};
const TAB_KEY_ALIAS_ENTRIES = Object.entries(TAB_KEY_BY_ALIAS);

const decodeHtmlEntities = (value = '') => {
  return value
    .replaceAll('&nbsp;', ' ')
    .replaceAll('&amp;', '&')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'");
};

const sanitizeNotificationMessage = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const withLineBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n');

  const strippedHtml = withLineBreaks.replace(/<[^>]*>/g, ' ');
  const decoded = decodeHtmlEntities(strippedHtml);

  return decoded.replace(/\s+/g, ' ').trim();
};

// Like sanitizeNotificationMessage but preserves <b> tags and replaces <img> with [[SOCO_POINT_ICON]]
const sanitizeNotificationMessageWithBold = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const withLineBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n');

  // Replace img tags (soco point icon) with placeholder before stripping
  const withIconPlaceholder = withLineBreaks.replace(
    /<img[^>]*>/gi,
    '[[SOCO_POINT_ICON]]',
  );

  // Strip all tags except <b> and </b>
  const preserved = withIconPlaceholder.replace(/<(?!\/?b\b)[^>]*>/gi, ' ');
  const decoded = decodeHtmlEntities(preserved);

  return decoded.replace(/ {2,}/g, ' ').trim();
};

const buildAllNotifications = (notificationsByTab) => {
  const flatList = Object.values(notificationsByTab).flat();
  return sortNotificationsByNewest(flatList);
};

const normalizeImageSource = (source) => {
  if (!source) {
    return null;
  }

  if (typeof source === 'number') {
    return source;
  }

  if (typeof source === 'string') {
    return source ? { uri: source } : null;
  }

  if (typeof source === 'object') {
    if (typeof source.uri === 'string') {
      return { uri: source.uri };
    }

    if (typeof source.url === 'string') {
      return { uri: source.url };
    }
  }

  return null;
};

const normalizeTabKey = (rawItem) => {
  const verb = String(rawItem?.verb || '').toLowerCase();
  const normalizedVerb = verb.replaceAll('-', '_');
  const type = String(rawItem?.type || '').toLowerCase();
  const objectType = String(rawItem?.object?.type || '').toLowerCase();
  if (
    normalizedVerb === 'earn_points' ||
    normalizedVerb === 'rate_and_review' ||
    normalizedVerb === 'points_expiring_soon' ||
    type === 'approved-affiliate' ||
    (normalizedVerb === 'approved' && objectType === 'affiliate') ||
    (normalizedVerb === 'earned' && objectType === 'benefit')
  ) {
    return 'loyalty';
  }

  const rawCandidates = [
    rawItem?.tab,
    rawItem?.notification_category,
    rawItem?.type,
    rawItem?.notification_type,
    rawItem?.category,
    rawItem?.segment,
    rawItem?.object?.type,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  for (let index = 0; index < rawCandidates.length; index += 1) {
    const candidate = rawCandidates[index];
    for (
      let aliasIndex = 0;
      aliasIndex < TAB_KEY_ALIAS_ENTRIES.length;
      aliasIndex += 1
    ) {
      const [aliasKey, tabKey] = TAB_KEY_ALIAS_ENTRIES[aliasIndex];
      if (candidate.includes(aliasKey)) {
        return tabKey;
      }
    }
  }

  return 'info';
};

const getDateValue = (notification) => {
  return (
    notification?.created_at ||
    notification?.createdAt ||
    notification?.published_at ||
    notification?.publishedAt ||
    notification?.updated_at ||
    notification?.updatedAt ||
    ''
  );
};

const formatNotificationTime = (rawDate) => {
  if (!rawDate) {
    return '';
  }

  const date = dayjs(rawDate);

  if (!date.isValid()) {
    return typeof rawDate === 'string' ? rawDate : '';
  }

  const now = dayjs();
  if (date.isSame(now, 'day')) {
    return date.format('HH:mm');
  }

  if (date.isSame(now.subtract(1, 'day'), 'day')) {
    return 'Yesterday';
  }

  return date.format('DD MMM YYYY');
};

const sortNotificationsByNewest = (notifications = []) => {
  if (notifications.length < 2) {
    return [...notifications];
  }

  return [...notifications].sort((left, right) => {
    const leftDate = dayjs(left.createdAt);
    const rightDate = dayjs(right.createdAt);
    const leftTs = leftDate.isValid() ? leftDate.valueOf() : 0;
    const rightTs = rightDate.isValid() ? rightDate.valueOf() : 0;
    return rightTs - leftTs;
  });
};

const isUnreadNotification = (rawItem) => {
  if (typeof rawItem?.unread === 'boolean') {
    return rawItem.unread;
  }

  if (typeof rawItem?.seen === 'boolean') {
    return !rawItem.seen;
  }

  if (typeof rawItem?.is_read === 'boolean') {
    return !rawItem.is_read;
  }

  if (typeof rawItem?.isRead === 'boolean') {
    return !rawItem.isRead;
  }

  if (typeof rawItem?.read === 'boolean') {
    return !rawItem.read;
  }

  return false;
};

const isAwaitingPaymentStatus = (status) =>
  /^awaiting\b.*\bpayment$/i.test(String(status || '').trim());

const setTitleButtonOrderStatus = (notification) => {
  const isRefund =
    notification?.object?.is_partial_refund ||
    /refund/gi.test(notification?.object?.refund_state)
      ? true
      : false;
  const orderStatus = notification?.object?.state?.name
    ? notification.object.state.name
    : '';
  const isVirtualAccount = /virtual_account/gi.test(
    notification?.object?.payment_method?.code,
  )
    ? notification.object.payment_method.code
    : '';
  const isShopeepay =
    /shopeepay/gi.test(notification?.object?.payment_method?.code) ||
    /shopeepay/gi.test(orderStatus);

  const redirectionAction =
    notification?.redirection?.action ||
    notification?.object?.redirection?.action;
  const canConfirmPackage =
    redirectionAction === 'open_confirm_package_received_popup' ||
    Boolean(notification?.object?.can_package_received_order);
  let title = '';

  if (isAwaitingPaymentStatus(orderStatus) && !isShopeepay) {
    title = 'Pay Now';
  }

  switch (orderStatus) {
    case 'Payment Failed':
    case 'Payment Expired':
    case 'Cancelled':
      title = 'Reorder';
      break;
    case 'Shipped':
      title = canConfirmPackage ? 'Confirm Package' : 'Track Order';
      break;
    case 'Ready to Pickup':
      title = 'Pick Up Now';
      break;
    case 'Waiting for Refund Details':
    case 'Refund':
    case 'Partial Refund Requested':
      title = 'Fill refund form';
      break;
  }

  if (
    isRefund &&
    orderStatus !== 'Refunded' &&
    orderStatus !== 'Refund Failed' &&
    notification.title_text !== 'Refund Successful'
  ) {
    title = 'Fill refund form';
  }

  if (isRefund && orderStatus === 'Payment') {
    if (title === 'Pay Now' && !isVirtualAccount) {
      return '';
    }
  }

  return title;
};

const getEarnPointsAddReviewContent = (notification) => {
  const pointsValue = notification?.object?.points_value;
  if (!pointsValue) {
    return null;
  }
  return `Moms, kamu mendapatkan <b>${pointsValue} SOCO Points</b> dari Shopper Review yang kamu buat`;
};

const getAwaitingPaymentContent = (objectReferenceNo) => {
  if (!objectReferenceNo) {
    return null;
  }
  return `Selesaikan pembayaranmu untuk Order <b>${objectReferenceNo}</b>`;
};

const mapNotificationItem = (notification, index) => {
  const verb = String(notification?.verb || '').toLowerCase();
  const normalizedVerb = verb.replaceAll('-', '_');
  const tab = normalizeTabKey(notification);
  const createdAt = getDateValue(notification);
  const objectStateName = notification?.object?.state?.name;
  const objectReferenceNo = notification?.object?.reference_no;
  const objectType = notification?.object?.type;
  const isApprovedAffiliate =
    String(notification?.type || '').toLowerCase() === 'approved-affiliate' ||
    (normalizedVerb === 'approved' &&
      String(objectType || '').toLowerCase() === 'affiliate');
  const isEarnedBenefit =
    normalizedVerb === 'earned' &&
    String(objectType || '').toLowerCase() === 'benefit';
  const titleButtonOrderStatus = setTitleButtonOrderStatus(notification);
  const isEarnPointsAddReview =
    normalizedVerb === 'earn_points' && objectType === 'add-review';
  const earnPointsAddReviewContent = isEarnPointsAddReview
    ? getEarnPointsAddReviewContent(notification)
    : null;
  const redirection =
    notification?.redirection ||
    notification?.redirect ||
    notification?.object?.redirection;
  const orderStatus =
    notification?.order_status ||
    notification?.object?.order_status ||
    notification?.object?.soco_state ||
    objectStateName ||
    null;
  const awaitingPaymentContent = isAwaitingPaymentStatus(objectStateName)
    ? getAwaitingPaymentContent(objectReferenceNo)
    : null;
  const orderIdCandidate =
    notification?.object?._id ||
    notification?.object?.id ||
    notification?.target?.id ||
    notification?.id_order ||
    null;
  const orderPayload = {
    ...(notification?.object || {}),
    _id:
      notification?.object?._id ||
      notification?.object?.id ||
      notification?.target?.id ||
      null,
    id:
      notification?.object?.id ||
      notification?.object?._id ||
      notification?.target?.id ||
      null,
    soco_state:
      notification?.object?.soco_state ||
      objectStateName ||
      notification?.order_status ||
      null,
    order_status:
      notification?.order_status ||
      notification?.object?.order_status ||
      objectStateName ||
      null,
  };

  const thumbnailCandidate =
    notification?.thumbnail ||
    notification?.thumbnail_url ||
    notification?.thumbnailUrl ||
    notification?.product_image ||
    notification?.object?.product_image ||
    notification?.object?.products?.[0]?.image ||
    notification?.image_url ||
    notification?.imageUrl ||
    null;

  const bannerCandidate =
    notification?.banner ||
    notification?.banner_url ||
    notification?.bannerUrl ||
    null;

  const id =
    notification?.id ||
    notification?._id ||
    notification?.notification_id ||
    `${tab}-${createdAt || index}`;

  return {
    id: String(id),
    tab,
    title: isEarnPointsAddReview
      ? 'Points Earned'
      : notification?.title ||
        notification?.title_text ||
        notification?.message_title ||
        notification?.notification_title ||
        notification?.object?.title_text ||
        notification?.object?.title ||
        'Notification',
    message: sanitizeNotificationMessage(
      earnPointsAddReviewContent ||
        notification?.message ||
        notification?.content ||
        notification?.message_body ||
        notification?.notification_body ||
        notification?.body ||
        notification?.object?.content ||
        notification?.object?.message ||
        awaitingPaymentContent ||
        (objectReferenceNo && objectStateName
          ? `Order ${objectReferenceNo} ${objectStateName}`
          : '') ||
        '',
    ),
    rawMessage: sanitizeNotificationMessageWithBold(
      earnPointsAddReviewContent ||
        notification?.message ||
        notification?.content ||
        notification?.message_body ||
        notification?.notification_body ||
        notification?.body ||
        notification?.object?.content ||
        notification?.object?.message ||
        awaitingPaymentContent ||
        (objectReferenceNo && objectStateName
          ? `Order ${objectReferenceNo} ${objectStateName}`
          : '') ||
        '',
    ),
    unread: isUnreadNotification(notification),
    thumbnail: normalizeImageSource(thumbnailCandidate),
    banner: normalizeImageSource(bannerCandidate),
    actionLabel:
      (normalizedVerb === 'rate_and_review' ? 'Write review' : null) ||
      (isApprovedAffiliate ? 'Share & Earn' : null) ||
      (isEarnedBenefit ? 'Check My Benefit' : null) ||
      notification?.actionLabel ||
      notification?.action_label ||
      notification?.button_text ||
      notification?.cta_label ||
      titleButtonOrderStatus,
    notification_category:
      normalizedVerb === 'earn_points'
        ? 'loyalty'
        : notification?.notification_category || notification?.category || tab,
    object_type: objectType,
    object_id: notification?.object?.id || notification?.object?._id || null,
    cart_id: notification?.object?.cart_id || notification?.cart_id || null,
    order_id: orderIdCandidate,
    order_type: notification?.object?.order_type || null,
    order_status: orderStatus,
    order_payload: orderPayload,
    object_state_name: objectStateName || null,
    object_soco_state: notification?.object?.soco_state || null,
    can_package_received_order: Boolean(
      notification?.object?.can_package_received_order,
    ),
    redirection,
    type: notification?.type || null,
    verb: notification?.verb || null,
    notification_type: notification?.notification_type || null,
    createdAt,
    time: formatNotificationTime(createdAt || notification?.time),
  };
};

const extractNotificationItems = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.notifications)) {
    return payload.notifications;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload?.data?.items)) {
    return payload.data.items;
  }

  if (Array.isArray(payload?.data?.notifications)) {
    return payload.data.notifications;
  }

  return [];
};

const mapNotificationsByTab = (payload) => {
  const rawItems = extractNotificationItems(payload);
  const groupedNotifications = getEmptyNotificationsByTab();

  rawItems.forEach((rawItem, index) => {
    const mapped = mapNotificationItem(rawItem, index);
    const tab = TAB_KEYS.includes(mapped.tab) ? mapped.tab : 'info';
    groupedNotifications[tab].push(mapped);
  });

  const notificationsByTab = getEmptyNotificationsByTab();

  TAB_KEYS.forEach((key) => {
    notificationsByTab[key] = sortNotificationsByNewest(
      groupedNotifications[key],
    );
  });

  return notificationsByTab;
};

const mergeNotificationsByTab = (currentByTab, incomingByTab) => {
  const notificationsByTab = getEmptyNotificationsByTab();
  let uniqueIncomingCount = 0;

  TAB_KEYS.forEach((key) => {
    const currentList = currentByTab[key] || [];
    const incomingList = incomingByTab[key] || [];
    if (!incomingList.length) {
      notificationsByTab[key] = currentList;
      return;
    }

    const currentIdSet = new Set(currentList.map((item) => item.id));
    const newItems = [];

    incomingList.forEach((item) => {
      if (!currentIdSet.has(item.id)) {
        currentIdSet.add(item.id);
        newItems.push(item);
        uniqueIncomingCount += 1;
      }
    });

    if (!newItems.length) {
      notificationsByTab[key] = currentList;
      return;
    }

    notificationsByTab[key] = sortNotificationsByNewest([
      ...currentList,
      ...newItems,
    ]);
  });

  return { notificationsByTab, uniqueIncomingCount };
};

const getNotificationItems = (payload) => {
  return extractNotificationItems(payload);
};

const markAllNotificationsAsRead = (notificationsByTab) => {
  return Object.keys(notificationsByTab).reduce((updatedState, key) => {
    const updatedList = notificationsByTab[key].map((item) => ({
      ...item,
      unread: false,
    }));

    return {
      ...updatedState,
      [key]: updatedList,
    };
  }, {});
};

const hasUnreadNotifications = (notificationsByTab) => {
  return Object.values(notificationsByTab).some((list) =>
    list.some((item) => item.unread),
  );
};

const getUnreadCountByTab = (notificationsByTab, tabKey) => {
  return (notificationsByTab[tabKey] || []).filter((item) => item.unread)
    .length;
};

export {
  DEFAULT_NOTIFICATION_BY_TAB,
  buildAllNotifications,
  getNotificationItems,
  mapNotificationsByTab,
  mergeNotificationsByTab,
  markAllNotificationsAsRead,
  hasUnreadNotifications,
  getUnreadCountByTab,
  isAwaitingPaymentStatus,
};
